import { X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import './token-input.css'
import { usePopover } from './usePopover'

export type TokenSuggestion = { value: string; label: string }

/**
 * An ORDERED list of values, built one at a time from a suggested set that the
 * caller may also add to.
 *
 * It exists for the half-bounded vocabulary: a field where some values are a
 * known set and the rest are whatever this shul says. A `Select` states one
 * answer and hides the alternatives, and a `Choice` shows a closed set and
 * cannot take a new item — neither can express "these seven, or anything else
 * you like, in this order".
 *
 * Order is part of the value, not a presentation of it: "Kabbalas Shabbos &
 * Maariv" is those two in that sequence, so tokens keep the order they were
 * added and a checkbox list could not express it.
 *
 * **It refuses nothing.** The suggestions are an offer, never a constraint —
 * anything typed is accepted. The control's job is to make the known names the
 * easy path, not to police the unknown ones.
 *
 * The caller owns both directions of the translation, so the primitive stays
 * ignorant of any one vocabulary: `format` says how a stored value reads, and
 * `normalize` says what typed words are stored as.
 */
export function TokenInput({
  value,
  onValueChange,
  suggestions = [],
  format = (v) => v,
  normalize = (v) => v.trim(),
  placeholder,
  id,
  ariaLabel,
}: {
  value: string[]
  onValueChange: (next: string[]) => void
  /** Offered first, and never enforced. */
  suggestions?: TokenSuggestion[]
  /** How a stored value is shown, on a token and in the list. */
  format?: (value: string) => string
  /** What a typed word becomes when stored. */
  normalize?: (typed: string) => string
  placeholder?: string
  id?: string
  ariaLabel?: string
}) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const optionId = useId()
  const { open, setOpen, ref, panelRef } = usePopover()

  /* Already-chosen values are not offered again — a token list is a set in what
     it may contain even though it is a sequence in how it reads. */
  const offered = suggestions.filter((s) => !value.includes(s.value))
  const q = query.trim().toLowerCase()
  const matches = q
    ? offered.filter(
        (s) =>
          s.label.toLowerCase().includes(q) ||
          s.value.toLowerCase().includes(q),
      )
    : offered

  /* What Enter would add when no suggestion is highlighted. Shown as its own
     row so that adding an unlisted word is a visible act rather than a thing
     that happens to work. */
  const typedValue = query.trim() ? normalize(query) : ''
  const canAddTyped = !!typedValue && !value.includes(typedValue)

  const add = (v: string) => {
    if (!v || value.includes(v)) return
    onValueChange([...value, v])
    setQuery('')
    setActive(0)
    inputRef.current?.focus()
  }

  const removeAt = (i: number) => {
    onValueChange(value.filter((_, n) => n !== i))
    inputRef.current?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    /* Backspace on an empty field takes the last token — the one gesture people
       arrive already knowing from every other control of this shape. */
    if (e.key === 'Backspace' && !query && value.length) {
      e.preventDefault()
      removeAt(value.length - 1)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const last = matches.length - 1 + (canAddTyped ? 1 : 0)
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActive((i) => Math.min(last, Math.max(0, i + delta)))
      return
    }
    /* Enter belongs to this control while it has something to add, and to the
       form otherwise — a field that swallowed every Enter would make a token
       list the one place a form cannot be submitted from. */
    if (e.key === 'Enter') {
      const hit = matches[active]
      if (open && hit) {
        e.preventDefault()
        add(hit.value)
      } else if (canAddTyped) {
        e.preventDefault()
        add(typedValue)
      }
      return
    }
    /* A comma ends a token, because the value reads as a list and that is how a
       list is typed. */
    if (e.key === ',' && canAddTyped) {
      e.preventDefault()
      add(typedValue)
    }
  }

  return (
    <div className="tokens" ref={ref}>
      {/* The box is a control by appearance and a group by role: the tokens are
          not text inside the field, they are values beside it. It carries no
          click handler of its own — the input and the remove buttons are the
          things worth hitting, and a div that focuses on click is a control
          the keyboard cannot reach. */}
      <div className="control tokens__box" data-open={open ? '' : undefined}>
        <ul className="tokens__list">
          {value.map((v, i) => (
            <li className="tokens__chip" key={v}>
              <span className="tokens__chip-label">{format(v)}</span>
              <button
                type="button"
                className="tokens__remove"
                aria-label={`Remove ${format(v)}`}
                onClick={(e) => {
                  e.stopPropagation()
                  removeAt(i)
                }}
              >
                <X aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <input
          ref={inputRef}
          id={id}
          className="tokens__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-activedescendant={
            open && matches[active] ? `${optionId}-${active}` : undefined
          }
          placeholder={value.length ? '' : placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
            setOpen(true)
          }}
        />
      </div>

      {open && (matches.length > 0 || canAddTyped) && (
        <div className="tokens__pop" id={listId} role="listbox" ref={panelRef}>
          {matches.map((s, i) => (
            <button
              type="button"
              key={s.value}
              id={`${optionId}-${i}`}
              role="option"
              aria-selected={i === active}
              className="tokens__option"
              data-active={i === active ? '' : undefined}
              onMouseEnter={() => setActive(i)}
              onClick={() => add(s.value)}
            >
              {s.label}
            </button>
          ))}
          {canAddTyped && (
            <button
              type="button"
              id={`${optionId}-${matches.length}`}
              role="option"
              aria-selected={active === matches.length}
              className="tokens__option tokens__option--new"
              data-active={active === matches.length ? '' : undefined}
              onMouseEnter={() => setActive(matches.length)}
              onClick={() => add(typedValue)}
            >
              Add “{format(typedValue)}”
            </button>
          )}
        </div>
      )}
    </div>
  )
}
